import { useQuery } from "@tanstack/react-query";

import { foodVersionAPI } from "@/lib/food-version-api";
import { queryKeys } from "@/lib/query-keys";

/**
 * Version history for one food (ZCS-463).
 *
 * Console-only on the server — a patient token gets 403 — and it resolves for
 * a retired or soft-deleted food, unlike the food detail read. History exists
 * to explain plans that already exist, and a retired food is exactly the case
 * where the plans outlive the food.
 *
 * `retry: false` on purpose: until the versioning branch is deployed these
 * endpoints 404, and a caller showing "not available yet" wants that answer
 * immediately rather than after three backoffs.
 */
export const useFoodVersions = (foodItemId: number, enabled = true) =>
  useQuery({
    queryKey: queryKeys.nutrition.foodItems.versions(foodItemId),
    queryFn: () => foodVersionAPI.versions(foodItemId),
    enabled: enabled && Number.isFinite(foodItemId),
    retry: false,
    staleTime: 1000 * 30,
  });

/** One historical version in full, as that version recorded it. Same access rules as the list. */
export const useFoodVersion = (
  foodItemId: number,
  versionNumber: number | null
) =>
  useQuery({
    queryKey: queryKeys.nutrition.foodItems.version(
      foodItemId,
      versionNumber ?? -1
    ),
    queryFn: () => foodVersionAPI.version(foodItemId, versionNumber as number),
    enabled: Number.isFinite(foodItemId) && versionNumber != null,
    retry: false,
  });
